"use client";

import { AnimatePresence, m, type Transition } from "motion/react";
import { useState } from "react";
import DemoFooter from "@/components/preview/demo-footer";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { Button } from "@/components/ui/button";
import { ResultBadge } from "@/components/ui/result-badge";
import { cn } from "@/lib/utils";

type ExitMode = "soft" | "full";

const EXIT_SPRING: Transition = { bounce: 0, duration: 0.45, type: "spring" };
const LABEL_TRANSITION: Transition = { duration: 0.2, ease: "easeOut" };

function ExitVariant({
  isOpen,
  mode,
  onClose,
}: {
  isOpen: boolean;
  mode: ExitMode;
  onClose: () => void;
}) {
  const isSoft = mode === "soft";

  return (
    <div
      className={cn(
        "flex flex-col border-preview-border",
        isSoft && "border-t sm:border-t-0 sm:border-l"
      )}
    >
      <div className="relative flex h-64 w-full items-center justify-center overflow-hidden">
        <AnimatePresence initial={false} mode="popLayout">
          {isOpen && (
            <m.div
              animate={{ opacity: 1 }}
              className="absolute inset-0 z-10 size-full bg-preview-bg"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              key="backdrop"
              onClick={onClose}
              transition={EXIT_SPRING}
            />
          )}
          {isOpen && (
            <m.div
              animate={{ filter: "blur(0px)", opacity: 1, x: 0 }}
              className={cn(
                "absolute inset-y-1 z-20 flex w-56 flex-col gap-1 rounded-lg bg-gray-100 p-1 shadow-custom",
                isSoft ? "right-1" : "left-1"
              )}
              exit={{
                filter: isSoft ? "blur(4px)" : "blur(0px)",
                opacity: isSoft ? 0 : 1,
                x: isSoft ? "70%" : "calc(-100% - 4px)",
              }}
              initial={{
                filter: "blur(0px)",
                opacity: 1,
                x: isSoft ? "calc(100% + 4px)" : "calc(-100% - 4px)",
              }}
              key="drawer"
              transition={EXIT_SPRING}
            />
          )}
        </AnimatePresence>
        <ResultBadge
          bad={!isSoft}
          className={cn(
            "absolute bottom-3.5 z-30",
            isSoft ? "left-3.5" : "right-3.5"
          )}
        />
      </div>
    </div>
  );
}

export default function AnimationSubtleExitsDemo() {
  const [isOpen, setIsOpen] = useState(false);

  const close = () => setIsOpen(false);
  const label = isOpen ? "Close" : "Open";

  return (
    <DemoWrapper margin="">
      <div className="grid w-full grid-cols-1 sm:grid-cols-2">
        <ExitVariant isOpen={isOpen} mode="full" onClose={close} />
        <ExitVariant isOpen={isOpen} mode="soft" onClose={close} />
      </div>
      <DemoFooter>
        <Button
          onClick={() => setIsOpen((open) => !open)}
          rounded
          size="sm"
          type="button"
          variant="secondary"
        >
          <AnimatePresence initial={false} mode="popLayout">
            <m.span
              animate={{ filter: "blur(0px)", opacity: 1 }}
              className="whitespace-nowrap will-change-transform"
              exit={{ filter: "blur(4px)", opacity: 0 }}
              initial={{ filter: "blur(4px)", opacity: 0 }}
              key={label}
              transition={LABEL_TRANSITION}
            >
              {label}
            </m.span>
          </AnimatePresence>
        </Button>
      </DemoFooter>
    </DemoWrapper>
  );
}
